import useSWR, { mutate } from 'swr';
import api from '../../../services/api';
import type { KnowledgeBaseCategory } from '../useData';
import { fetchKnowledgeBaseCategories } from '../useData';

// Re-export the interface for convenience
export type { KnowledgeBaseCategory };

const KB_CATEGORIES_KEY = '/knowledge-base/categories';

// Fetch all categories (same key & fetcher as main useKnowledgeBaseCategories for consistent cache)
export const useKnowledgeBaseCategoriesList = () => {
  const { data, error, isLoading, mutate } = useSWR<KnowledgeBaseCategory[]>(
    KB_CATEGORIES_KEY,
    fetchKnowledgeBaseCategories,
    {
      revalidateOnFocus: true,
      revalidateOnReconnect: true,
    }
  );

  return {
    data: data || [],
    isLoading,
    error,
    mutate,
  };
};

// Fetch a single category
export const useKnowledgeBaseCategory = (id: number) => {
  const { data, error, isLoading, mutate } = useSWR<KnowledgeBaseCategory>(
    id ? `/knowledge-base/categories/${id}` : null,
    async (url: string) => {
      const response = await api.get(url);
      return response.data;
    },
    {
      revalidateOnFocus: true,
      revalidateOnReconnect: true,
    }
  );

  return {
    data,
    client: data, // GenericFormPage expects either data or client
    isLoading,
    error,
    mutate,
  };
};

// Create a new category
export const createKnowledgeBaseCategory = async (categoryData: Partial<KnowledgeBaseCategory>): Promise<KnowledgeBaseCategory> => {
  const response = await api.post('/knowledge-base/categories', categoryData);
  await mutate(KB_CATEGORIES_KEY, undefined, { revalidate: true });
  return response.data;
};

// Update a category
export const updateKnowledgeBaseCategory = async (id: number, categoryData: Partial<KnowledgeBaseCategory>): Promise<KnowledgeBaseCategory> => {
  const response = await api.patch(`/knowledge-base/categories/${id}`, categoryData);
  await mutate(`/knowledge-base/categories/${id}`);
  await mutate(KB_CATEGORIES_KEY, undefined, { revalidate: true });
  return response.data;
};

// Delete a category
export const deleteKnowledgeBaseCategory = async (id: number): Promise<void> => {
  await api.delete(`/knowledge-base/categories/${id}`);
  await mutate(KB_CATEGORIES_KEY, undefined, { revalidate: true });
};
